import { clsx } from 'clsx';
import { FC, MouseEventHandler, ReactNode } from 'react';

import { styled } from '@mui/material/styles';
import Box from '@o2/ui/Box';
import Stack from '@o2/ui/Stack';

import colors from '@o2/ui/theme/colors';

import Container, { ContainerProps } from '../Container';
import Backlink from './Backlink';
import ContentContainer from './ContentContainer';
import CustomContent from './CustomContent';
import Description from './Description';
import Filters from './Filters/Filters';
import SelectFilter from './Filters/SelectFilter';
import SwitchFilter from './Filters/SwitchFilter';
import HeadLeft from './HeadLeft';
import HeadRight from './HeadRight';
import subheaderClasses, { useSubheaderUtilityClasses } from './subheaderClasses';
import Tab from './Tabs/Tab';
import Tabs from './Tabs/Tabs';
import Title from './Title';
import { BoxProps } from '@mui/material';

export interface SubheaderProps extends Omit<BoxProps, 'title'> {
  /**
   * Whether to display a backlink.
   *
   * Anything set in this prop will be overridden if any children are passed! You can use `Subheader.Backlink` to replicate
   * this prop's functionality when using children.
   */
  backlink?: boolean;

  /**
   * Backlink label for accessibility attributes.
   */
  backlinkLabel?: string;

  /**
   * If any children are passed, other props of this component will have no effect. See the component documentation to find
   * out how to replicate their functionality using `Subheader.*` subcomponents.
   */
  children?: ReactNode;

  /**
   * Description to be rendered as `Subheader.Description`.
   *
   * Anything set in this prop will be overridden if any children are passed! You can use `Subheader.Title` to replicate
   * this prop's functionality when using children.
   */
  description?: ReactNode;

  /**
   * @see DescriptionProps.disableStyling
   */
  disableDescriptionStyling?: boolean;

  /**
   * @see TitleProps.disableStyling
   */
  disableTitleStyling?: boolean;

  /**
   * Subheader includes a `@o2/common-business/Container`. You can set this container's `maxWidth`, or fall back to the
   * default 'full-wide'.
   */
  maxWidth?: ContainerProps['variant'];

  /**
   * Click handler for the backlink which has to be enabled using the `backlink` prop.
   *
   * Anything set in this prop will be overridden if any children are passed! You can use `Subheader.Backlink` to replicate
   * this prop's functionality when using children.
   */
  onBacklinkClick?: MouseEventHandler;

  /**
   * Title to be rendered as `Subheader.Title`.
   *
   * Anything set in this prop will be overridden if any children are passed! You can use `Subheader.Title` to replicate
   * this prop's functionality when using children.
   */
  title?: ReactNode;
}

const SubheaderRoot = styled(Box, {
  name: 'O2Subheader',
  slot: 'Root',
})(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    borderBottom: `1px solid ${colors.transparentGray['200']}`,
    background: theme.palette.common.white,
    [`& .${subheaderClasses.tabs}`]: {
      marginBottom: theme.spacing(-2),
    },
  },
}));

type Subheader = FC<SubheaderProps> & {
  Backlink: typeof Backlink;
  ContentContainer: typeof ContentContainer;
  CustomContent: typeof CustomContent;
  Description: typeof Description;
  Filters: typeof Filters;
  HeadLeft: typeof HeadLeft;
  HeadRight: typeof HeadRight;
  SelectFilter: typeof SelectFilter;
  SwitchFilter: typeof SwitchFilter;
  Tab: typeof Tab;
  Tabs: typeof Tabs;
  Title: typeof Title;
};

const Subheader: Subheader = ({
  backlink,
  backlinkLabel,
  children,
  description,
  onBacklinkClick,
  title,
  disableDescriptionStyling = typeof description !== 'string',
  disableTitleStyling = typeof title !== 'string',
  maxWidth = 'full-wide',
  ...other
}) => {
  const utilityClasses = useSubheaderUtilityClasses();

  return (
    <SubheaderRoot className={clsx(utilityClasses.root)} {...other}>
      <Container disableMarginY variant={maxWidth}>
        <Stack
          px={{ xs: 1, md: 0 }}
          py={{ xs: 3, md: 2 }}
          gap={{ xs: 2, md: 4 }}
          direction={{
            xs: 'column',
            md: 'row',
          }}
        >
          {children ?? (
            <>
              {backlink && <Backlink onClick={onBacklinkClick} aria-label={backlinkLabel} />}
              {(title || description) && (
                <ContentContainer>
                  <HeadLeft>
                    <Title disableStyling={disableTitleStyling}>{title}</Title>
                    {description && <Description disableStyling={disableDescriptionStyling}>{description}</Description>}
                  </HeadLeft>
                </ContentContainer>
              )}
            </>
          )}
        </Stack>
      </Container>
    </SubheaderRoot>
  );
};

Subheader.Backlink = Backlink;
Subheader.ContentContainer = ContentContainer;
Subheader.CustomContent = CustomContent;
Subheader.Description = Description;
Subheader.Filters = Filters;
Subheader.HeadLeft = HeadLeft;
Subheader.HeadRight = HeadRight;
Subheader.SelectFilter = SelectFilter;
Subheader.SwitchFilter = SwitchFilter;
Subheader.Tab = Tab;
Subheader.Tabs = Tabs;
Subheader.Title = Title;

export default Subheader;
